/**
 * The mark along the bottom of every frame: "Selfeed".
 *
 * The phone puts it on every film it makes and Pro takes it off; a film
 * made here carried nothing, which handed the one benefit Pro sells to
 * anybody with a browser, and made the two clients' films different
 * things. The geometry is the phone's, from `Theme.Export` in
 * ios/Shared/Theme/Tokens.swift — a test reads the numbers back out of
 * that file — and every ratio is of the canvas's shorter side, so the
 * mark keeps its proportions across the three frame shapes instead of
 * growing when the canvas gets taller.
 *
 * The halo is what makes one mark legible over a bright frame and a dark
 * one: pale ink alone disappears into a face lit from the front, which
 * is most selfies taken indoors. At this blur and this opacity it is not
 * a glow but a keyline, two pixels of near-opaque dark hugging the
 * letterforms — the phone measured it, and the same numbers hold here.
 */
export const WATERMARK = {
  text: "Selfeed",
  /** The type size, as a fraction of the canvas's shorter side. */
  sizeRatio: 0.034,
  /** How far the mark sits above the bottom edge. */
  insetRatio: 0.028,
  /** The halo's blur, as a fraction of the type size. */
  haloBlurRatio: 0.15,
  /** The dark palette's foreground, present and not shouting. */
  ink: "rgba(244, 237, 230, 0.92)",
  /** The dark palette's scrim, nearly opaque: the keyline. */
  halo: "rgba(0, 0, 0, 0.85)",
  weight: 600,
} as const;

export type WatermarkLayout = {
  size: number;
  /** Where the baseline sits, from the top; the descent is kept above the inset. */
  baseline: number;
  blur: number;
  font: string;
};

/** Where and how large the mark is on a canvas of this size. */
export function watermarkLayout(
  width: number,
  height: number,
  descentOf: (font: string) => number,
): WatermarkLayout {
  const shortSide = Math.min(width, height);
  const size = Math.round(shortSide * WATERMARK.sizeRatio);
  const font = `${WATERMARK.weight} ${size}px system-ui, -apple-system, sans-serif`;
  const inset = shortSide * WATERMARK.insetRatio;
  return {
    size,
    baseline: Math.round(height - inset - descentOf(font)),
    blur: size * WATERMARK.haloBlurRatio,
    font,
  };
}

/** Draws the mark, centred along the bottom edge, over whatever is on the canvas. */
export function drawWatermark(context: CanvasRenderingContext2D, width: number, height: number) {
  const layout = watermarkLayout(width, height, (font) => {
    context.font = font;
    return context.measureText(WATERMARK.text).fontBoundingBoxDescent ?? 0;
  });
  context.save();
  context.font = layout.font;
  context.textAlign = "center";
  context.textBaseline = "alphabetic";
  context.shadowColor = WATERMARK.halo;
  context.shadowBlur = layout.blur;
  context.shadowOffsetX = 0;
  context.shadowOffsetY = 0;
  context.fillStyle = WATERMARK.ink;
  context.fillText(WATERMARK.text, Math.round(width / 2), layout.baseline);
  context.restore();
}
